import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { expenseAPI, projectAPI, vendorAPI, reportAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Plus,
  Search,
  Trash2,
  Wallet,
  IndianRupee,
  Calendar,
  Filter,
  Edit,
  Users,
  AlertCircle,
  CheckCircle,
  Clock,
  Receipt,
} from "lucide-react";

const EXPENSE_CATEGORIES = [
  { value: "travel", label: "Travel" },
  { value: "accommodation", label: "Accommodation" },
  { value: "printing_production", label: "Printing & Production" },
  { value: "venue_property", label: "Venue & Property" },
  { value: "outsourced_service", label: "Outsourced Service" },
  { value: "employee_reimbursement", label: "Employee Reimbursement" },
  { value: "petty_cash", label: "Petty Cash" },
  { value: "miscellaneous", label: "Miscellaneous" },
];

const PAYMENT_MODES = [
  { value: "cash", label: "Cash" },
  { value: "cheque", label: "Cheque" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "upi", label: "UPI" },
  { value: "credit_card", label: "Credit Card" },
  { value: "company_card", label: "Company Card" },
  { value: "other", label: "Other" },
];

const PAID_BY_TYPES = [
  { value: "company", label: "Company Account" },
  { value: "employee", label: "Employee (Personal)" },
];

const categoryColors = {
  travel: "bg-blue-100 text-blue-700",
  accommodation: "bg-purple-100 text-purple-700",
  printing_production: "bg-amber-100 text-amber-700",
  venue_property: "bg-emerald-100 text-emerald-700",
  outsourced_service: "bg-pink-100 text-pink-700",
  employee_reimbursement: "bg-cyan-100 text-cyan-700",
  petty_cash: "bg-orange-100 text-orange-700",
  miscellaneous: "bg-slate-100 text-slate-700",
};

export default function ExpensesPage() {
  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [projectFilter, setProjectFilter] = useState("all");
  const [reimbursementFilter, setReimbursementFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reimbursementDialogOpen, setReimbursementDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, total: 0, limit: 50 });
  const [expenseSummary, setExpenseSummary] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  
  const [formData, setFormData] = useState({
    description: "",
    amount: "",
    expense_date: new Date().toISOString().split("T")[0],
    category: "miscellaneous",
    project_id: "",
    vendor_id: "",
    payment_mode: "bank_transfer",
    reference_number: "",
    notes: "",
    is_billable: false,
    paid_by: "",
    paid_by_type: "company",
    attachment_url: "",
  });

  const [reimbursementData, setReimbursementData] = useState({
    is_reimbursed: true,
    reimbursed_to: "",
    reimbursement_date: new Date().toISOString().split("T")[0],
    reimbursement_reference: "",
    reimbursement_notes: "",
  });

  useEffect(() => {
    fetchExpenses();
    fetchProjects();
    fetchVendors();
    fetchExpenseSummary();
  }, [categoryFilter, projectFilter, reimbursementFilter, activeTab]);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const params = { page: pagination.page, limit: pagination.limit };
      if (categoryFilter !== "all") params.category = categoryFilter;
      if (projectFilter !== "all") params.project_id = projectFilter;
      
      // Handle reimbursement filters
      if (activeTab === "pending") {
        params.reimbursement_status = "pending";
      } else if (activeTab === "reimbursed") {
        params.reimbursement_status = "reimbursed";
      } else if (activeTab === "employee") {
        params.paid_by_type = "employee";
      }
      
      if (reimbursementFilter !== "all") {
        if (reimbursementFilter === "pending") {
          params.reimbursement_status = "pending";
        } else if (reimbursementFilter === "reimbursed") {
          params.is_reimbursed = true;
        }
      }
      
      const response = await expenseAPI.list(params);
      setExpenses(response.data.expenses || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data.total || 0,
      }));
    } catch (error) {
      toast.error("Failed to fetch expenses");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await projectAPI.list();
      setProjects(response.data);
    } catch (error) {
      console.error("Failed to fetch projects");
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await vendorAPI.list();
      setVendors(response.data);
    } catch (error) {
      console.error("Failed to fetch vendors");
    }
  };

  const fetchExpenseSummary = async () => {
    try {
      const response = await reportAPI.expenseSummary();
      setExpenseSummary(response.data);
    } catch (error) {
      console.error("Failed to fetch expense summary");
    }
  };

  const handleOpenDialog = (expense = null) => {
    if (expense) {
      setSelectedExpense(expense);
      setFormData({
        description: expense.description || "",
        amount: expense.amount || "",
        expense_date: expense.expense_date?.split("T")[0] || new Date().toISOString().split("T")[0],
        category: expense.category || "miscellaneous",
        project_id: expense.project_id || "",
        vendor_id: expense.vendor_id || "",
        payment_mode: expense.payment_mode || "bank_transfer",
        reference_number: expense.reference_number || "",
        notes: expense.notes || "",
        is_billable: expense.is_billable || false,
        paid_by: expense.paid_by || "",
        paid_by_type: expense.paid_by_type || "company",
        attachment_url: expense.attachment_url || "",
      });
    } else {
      setSelectedExpense(null);
      setFormData({
        description: "",
        amount: "",
        expense_date: new Date().toISOString().split("T")[0],
        category: "miscellaneous",
        project_id: "",
        vendor_id: "",
        payment_mode: "bank_transfer",
        reference_number: "",
        notes: "",
        is_billable: false,
        paid_by: "",
        paid_by_type: "company",
        attachment_url: "",
      });
    }
    setDialogOpen(true);
  };

  const handleOpenReimbursementDialog = (expense) => {
    setSelectedExpense(expense);
    setReimbursementData({
      is_reimbursed: true,
      reimbursed_to: expense.paid_by || "",
      reimbursement_date: new Date().toISOString().split("T")[0],
      reimbursement_reference: "",
      reimbursement_notes: "",
    });
    setReimbursementDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.description) {
      toast.error("Description is required");
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error("Valid amount is required");
      return;
    }
    if (formData.paid_by_type === "employee" && !formData.paid_by) {
      toast.error("Please enter employee name who paid");
      return;
    }

    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
        expense_date: new Date(formData.expense_date).toISOString(),
        project_id: formData.project_id || null,
        vendor_id: formData.vendor_id || null,
      };

      if (selectedExpense) {
        await expenseAPI.update(selectedExpense.id, data);
        toast.success("Expense updated successfully");
      } else {
        await expenseAPI.create(data);
        toast.success("Expense recorded successfully");
      }
      setDialogOpen(false);
      fetchExpenses();
      fetchExpenseSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save expense");
    }
  };

  const handleReimbursementSubmit = async () => {
    if (!reimbursementData.reimbursed_to) {
      toast.error("Please enter who received the reimbursement");
      return;
    }

    try {
      const data = {
        ...reimbursementData,
        reimbursement_date: new Date(reimbursementData.reimbursement_date).toISOString(),
      };

      await expenseAPI.update(selectedExpense.id, data);
      toast.success("Reimbursement recorded successfully");
      setReimbursementDialogOpen(false);
      fetchExpenses();
      fetchExpenseSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record reimbursement");
    }
  };

  const handleDelete = async () => {
    try {
      await expenseAPI.delete(selectedExpense.id);
      toast.success("Expense deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedExpense(null);
      fetchExpenses();
      fetchExpenseSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete expense");
    }
  };

  const filteredExpenses = expenses.filter((expense) =>
    expense.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    expense.expense_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    expense.paid_by?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getProjectName = (projectId) => {
    const project = projects.find((p) => p.id === projectId);
    return project?.name || "-";
  };

  const getVendorName = (vendorId) => {
    const vendor = vendors.find((v) => v.id === vendorId);
    return vendor?.name || "-";
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Calculate totals from current filtered list
  const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const pendingReimbursements = expenses
    .filter((e) => e.paid_by_type === "employee" && !e.is_reimbursed)
    .reduce((sum, e) => sum + (e.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Expenses</h1>
          <p className="text-slate-600 mt-1">Track expenses and employee reimbursements</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-expense-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Expense
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Wallet className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Expenses</p>
                <p className="text-2xl font-bold">{expenseSummary?.expense_count || expenses.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <IndianRupee className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Amount</p>
                <p className="text-2xl font-bold">{formatCurrency(expenseSummary?.total_expenses || totalExpenses)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-red-700">Pending Reimbursements</p>
                <p className="text-2xl font-bold text-red-700">
                  {formatCurrency(expenseSummary?.total_pending_reimbursements || pendingReimbursements)}
                </p>
                <p className="text-xs text-red-600">{expenseSummary?.pending_reimbursement_count || 0} expenses</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <Calendar className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">This Month</p>
                <p className="text-2xl font-bold">{formatCurrency(expenseSummary?.this_month_expenses || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reimbursements by Employee Widget */}
      {expenseSummary?.reimbursements_by_employee?.length > 0 && (
        <Card>
          <CardHeader className="py-4">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-5 h-5 text-red-500" />
              Pending Reimbursements by Team Member
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-wrap gap-3">
              {expenseSummary.reimbursements_by_employee.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <span className="font-medium text-red-800">{item.employee}</span>
                  <Badge variant="destructive">{formatCurrency(item.amount)}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs for different views */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All Expenses</TabsTrigger>
          <TabsTrigger value="employee" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Employee Paid
          </TabsTrigger>
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Pending Reimbursement
          </TabsTrigger>
          <TabsTrigger value="reimbursed" className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Reimbursed
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search expenses, paid by..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-expenses"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="category-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {EXPENSE_CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="project-filter">
                <SelectValue placeholder="Project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map((proj) => (
                  <SelectItem key={proj.id} value={proj.id}>
                    {proj.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Expenses Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Expense #</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Paid By</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-slate-200 border-t-slate-600 rounded-full animate-spin" />
                      Loading...
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredExpenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    No expenses found
                  </TableCell>
                </TableRow>
              ) : (
                filteredExpenses.map((expense) => (
                  <TableRow key={expense.id} data-testid={`expense-row-${expense.id}`}>
                    <TableCell>
                      <code className="text-sm bg-slate-100 px-2 py-0.5 rounded">
                        {expense.expense_number}
                      </code>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{expense.description}</p>
                        {expense.project_id && (
                          <p className="text-sm text-slate-500">
                            Project: {getProjectName(expense.project_id)}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={categoryColors[expense.category]}>
                        {EXPENSE_CATEGORIES.find((c) => c.value === expense.category)?.label || expense.category}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        {expense.paid_by_type === "employee" ? (
                          <div className="flex flex-col">
                            <span className="font-medium text-amber-700">{expense.paid_by || "Employee"}</span>
                            <span className="text-xs text-amber-600">Employee Paid</span>
                          </div>
                        ) : (
                          <span className="text-slate-600">Company Account</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(expense.expense_date)}</TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(expense.amount)}
                    </TableCell>
                    <TableCell>
                      {expense.paid_by_type === "employee" ? (
                        expense.is_reimbursed ? (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Reimbursed
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">
                            <Clock className="w-3 h-3 mr-1" />
                            Pending
                          </Badge>
                        )
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600">Company</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {expense.paid_by_type === "employee" && !expense.is_reimbursed && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenReimbursementDialog(expense)}
                            className="text-emerald-600 hover:text-emerald-700"
                            title="Mark as Reimbursed"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(expense)}
                          title="Edit"
                        >
                          <Edit className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedExpense(expense);
                            setDeleteDialogOpen(true);
                          }}
                          data-testid={`delete-expense-${expense.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Expense Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedExpense ? "Edit Expense" : "Add New Expense"}</DialogTitle>
            <DialogDescription>
              {selectedExpense ? "Update expense details" : "Record a new expense entry"}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter expense description"
                data-testid="expense-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount">Amount (₹) *</Label>
                <Input
                  id="amount"
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0"
                  data-testid="expense-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expense_date">Date</Label>
                <Input
                  id="expense_date"
                  type="date"
                  value={formData.expense_date}
                  onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                  data-testid="expense-date-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger data-testid="expense-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="payment_mode">Payment Mode</Label>
                <Select
                  value={formData.payment_mode}
                  onValueChange={(value) => setFormData({ ...formData, payment_mode: value })}
                >
                  <SelectTrigger data-testid="expense-payment-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_MODES.map((mode) => (
                      <SelectItem key={mode.value} value={mode.value}>
                        {mode.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Payment Done By Section */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-4">
              <div className="flex items-center gap-2 text-amber-800">
                <Users className="w-5 h-5" />
                <Label className="font-semibold text-amber-800">Payment Done By</Label>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="paid_by_type">Paid By Type *</Label>
                  <Select
                    value={formData.paid_by_type}
                    onValueChange={(value) => setFormData({ ...formData, paid_by_type: value })}
                  >
                    <SelectTrigger data-testid="expense-paid-by-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAID_BY_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="paid_by">
                    {formData.paid_by_type === "employee" ? "Employee Name *" : "Account/Card Holder"}
                  </Label>
                  <Input
                    id="paid_by"
                    value={formData.paid_by}
                    onChange={(e) => setFormData({ ...formData, paid_by: e.target.value })}
                    placeholder={formData.paid_by_type === "employee" ? "Enter employee name" : "Optional"}
                    data-testid="expense-paid-by-input"
                  />
                </div>
              </div>
              
              {formData.paid_by_type === "employee" && (
                <p className="text-sm text-amber-700">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  This expense will be tracked for reimbursement
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="project">Project</Label>
                <Select
                  value={formData.project_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, project_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="expense-project-select">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Project</SelectItem>
                    {projects.map((proj) => (
                      <SelectItem key={proj.id} value={proj.id}>
                        {proj.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="vendor">Vendor</Label>
                <Select
                  value={formData.vendor_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, vendor_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="expense-vendor-select">
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Vendor</SelectItem>
                    {vendors.map((vendor) => (
                      <SelectItem key={vendor.id} value={vendor.id}>
                        {vendor.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reference_number">Reference Number</Label>
              <Input
                id="reference_number"
                value={formData.reference_number}
                onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                placeholder="Transaction/receipt reference"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="attachment_url">Attachment/Receipt URL</Label>
              <Input
                id="attachment_url"
                value={formData.attachment_url}
                onChange={(e) => setFormData({ ...formData, attachment_url: e.target.value })}
                placeholder="URL to receipt or document"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes"
                rows={2}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_billable"
                checked={formData.is_billable}
                onChange={(e) => setFormData({ ...formData, is_billable: e.target.checked })}
                className="rounded border-slate-300"
              />
              <Label htmlFor="is_billable" className="text-sm font-normal">
                Mark as billable expense
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} data-testid="save-expense-btn">
              {selectedExpense ? "Update Expense" : "Save Expense"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark as Reimbursed Dialog */}
      <Dialog open={reimbursementDialogOpen} onOpenChange={setReimbursementDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-emerald-600" />
              Record Reimbursement
            </DialogTitle>
            <DialogDescription>
              Mark expense "{selectedExpense?.expense_number}" as reimbursed
            </DialogDescription>
          </DialogHeader>

          <div className="p-4 bg-slate-50 rounded-lg mb-4">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Expense Amount:</span>
              <span className="font-bold">{formatCurrency(selectedExpense?.amount)}</span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className="text-slate-500">Paid By:</span>
              <span className="font-medium">{selectedExpense?.paid_by || "Employee"}</span>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reimbursed_to">Reimbursed To *</Label>
              <Input
                id="reimbursed_to"
                value={reimbursementData.reimbursed_to}
                onChange={(e) => setReimbursementData({ ...reimbursementData, reimbursed_to: e.target.value })}
                placeholder="Enter employee name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="reimbursement_date">Reimbursement Date</Label>
              <Input
                id="reimbursement_date"
                type="date"
                value={reimbursementData.reimbursement_date}
                onChange={(e) => setReimbursementData({ ...reimbursementData, reimbursement_date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="reimbursement_reference">Reference Number</Label>
              <Input
                id="reimbursement_reference"
                value={reimbursementData.reimbursement_reference}
                onChange={(e) => setReimbursementData({ ...reimbursementData, reimbursement_reference: e.target.value })}
                placeholder="Payment reference, transaction ID, etc."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="reimbursement_notes">Notes</Label>
              <Textarea
                id="reimbursement_notes"
                value={reimbursementData.reimbursement_notes}
                onChange={(e) => setReimbursementData({ ...reimbursementData, reimbursement_notes: e.target.value })}
                placeholder="Additional notes"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReimbursementDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleReimbursementSubmit} className="bg-emerald-600 hover:bg-emerald-700">
              <CheckCircle className="w-4 h-4 mr-2" />
              Record Reimbursement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Expense</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete expense "{selectedExpense?.expense_number}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
